import { useQuery } from "@tanstack/react-query";
import {
  type FunctionComponent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { PublicAnnouncement } from "@/features/announce/api/announceClient";
import { createAnnouncementMetaQueryOptions } from "@/features/announce/hooks/announcementMetaQuery";
import {
  type AnnouncementStatus,
  AnnouncementStatusContext,
} from "@/features/announce/hooks/useAnnouncementStatus";
import {
  getFetchedAnnouncementsReadThroughRevision,
  getFetchedAnnouncementsReadThroughTimestamp,
} from "@/features/announce/lib/announcementReadReceipt";
import {
  advanceAnnouncementReadRevision,
  advanceAnnouncementReadTimestamp,
  announcementTimestampEpoch,
  applyAnnouncementReadRevisionStorageEvent,
  applyAnnouncementReadStorageEvent,
  isUnreadAnnouncement,
  isUnreadAnnouncementRevision,
  persistAnnouncementReadRevision,
  persistAnnouncementReadTimestamp,
  readAnnouncementReadRevision,
  readAnnouncementReadTimestamp,
} from "@/features/announce/lib/announcementReadState";

type AnnouncementReadTarget = Pick<
  PublicAnnouncement,
  "id" | "published_at" | "article_unread_revision"
>;

interface SessionAnnouncementRead {
  publishedAt: string | null;
  articleUnreadRevision: number;
}

/**
 * PC/SP共通のmeta queryと既読状態をアプリ枠の外側に1つだけ保持する。
 * レスポンシブ切替後もTanStack Queryの同一cacheを使い、二重pollを作らない。
 */
export const AnnouncementStatusProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }: PropsWithChildren) => {
  const metaQuery = useQuery(createAnnouncementMetaQueryOptions());
  const canReserveMeta =
    !metaQuery.isFetched &&
    metaQuery.isPending &&
    metaQuery.fetchStatus !== "paused";
  // 初回取得の完了、またはオフラインで予約を省いた後は空き幅を復活させない。
  const [hasReleasedMetaReservation, setHasReleasedMetaReservation] = useState(
    !canReserveMeta,
  );
  if (!hasReleasedMetaReservation && !canReserveMeta) {
    setHasReleasedMetaReservation(true);
  }
  const isAnnouncementMetaPending =
    canReserveMeta && !hasReleasedMetaReservation;
  const [readTimestamp, setReadTimestamp] = useState(
    readAnnouncementReadTimestamp,
  );
  const [readRevision, setReadRevision] = useState(
    readAnnouncementReadRevision,
  );
  const [readAnnouncements, setReadAnnouncements] = useState<
    ReadonlyMap<number, SessionAnnouncementRead>
  >(() => new Map());
  const articleTimestamp = metaQuery.data?.article ?? null;
  const articleUnreadRevision = metaQuery.data?.article_unread_revision ?? 0;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (event: StorageEvent): void => {
      setReadTimestamp((current) =>
        applyAnnouncementReadStorageEvent(current, event),
      );
      setReadRevision((current) =>
        applyAnnouncementReadRevisionStorageEvent(current, event),
      );
    };
    window.addEventListener("storage", handleStorage);
    return (): void => window.removeEventListener("storage", handleStorage);
  }, []);

  const markAnnouncementsReadThrough = useCallback(
    (
      publishedAt: string | null,
      readThroughRevision: number | null = null,
    ): void => {
      setReadAnnouncements((current) =>
        retainAnnouncementReadsAfter(current, publishedAt, readThroughRevision),
      );
      if (publishedAt !== null) {
        setReadTimestamp((current) => {
          const next = advanceAnnouncementReadTimestamp(current, publishedAt);
          if (next === current || next === null) return current;
          persistAnnouncementReadTimestamp(next);
          return next;
        });
      }
      setReadRevision((current) => {
        const next = advanceAnnouncementReadRevision(
          current,
          readThroughRevision,
        );
        if (next === current || next === null) return current;
        persistAnnouncementReadRevision(next);
        return next;
      });
    },
    [],
  );

  const markCurrentAnnouncementsRead = useCallback(
    (
      announcements:
        | readonly Pick<
            PublicAnnouncement,
            "article_unread_revision" | "published_at"
          >[]
        | undefined,
      snapshotRevision: number | null = null,
    ): void => {
      if (!metaQuery.isSuccess || !Array.isArray(announcements)) return;
      markAnnouncementsReadThrough(
        getFetchedAnnouncementsReadThroughTimestamp(announcements),
        getFetchedAnnouncementsReadThroughRevision(
          announcements,
          snapshotRevision,
        ),
      );
    },
    [markAnnouncementsReadThrough, metaQuery.isSuccess],
  );

  const isAnnouncementRead = useCallback(
    (announcement: AnnouncementReadTarget): boolean => {
      const itemRevision = announcement.article_unread_revision ?? 0;
      const sessionRead = readAnnouncements.get(announcement.id);
      if (itemRevision > 0) {
        return (
          itemRevision <= (readRevision ?? 0) ||
          (sessionRead?.articleUnreadRevision ?? 0) >= itemRevision
        );
      }
      const publishedEpoch = announcementTimestampEpoch(
        announcement.published_at,
      );
      if (publishedEpoch === null) return true;
      const readEpoch = announcementTimestampEpoch(readTimestamp);
      return (
        (readEpoch !== null && publishedEpoch <= readEpoch) ||
        sessionRead !== undefined
      );
    },
    [readAnnouncements, readRevision, readTimestamp],
  );

  const markAnnouncementRead = useCallback(
    (announcement: AnnouncementReadTarget): void => {
      const { published_at: publishedAt } = announcement;
      const itemRevision = announcement.article_unread_revision ?? 0;
      const publishedEpoch = announcementTimestampEpoch(publishedAt);
      const readEpoch = announcementTimestampEpoch(readTimestamp);
      const needsRevisionReceipt = itemRevision > (readRevision ?? 0);
      const needsTimestampReceipt =
        publishedAt !== null &&
        publishedEpoch !== null &&
        (readEpoch === null || publishedEpoch > readEpoch);
      if (!needsRevisionReceipt && !needsTimestampReceipt) return;
      setReadAnnouncements((current) => {
        const previous = current.get(announcement.id);
        if (
          previous !== undefined &&
          previous.articleUnreadRevision >= itemRevision &&
          previous.publishedAt === publishedAt
        ) {
          return current;
        }
        const next = new Map(current);
        next.set(announcement.id, {
          publishedAt,
          articleUnreadRevision: itemRevision,
        });
        return next;
      });
    },
    [readRevision, readTimestamp],
  );

  const value = useMemo<AnnouncementStatus>(
    () => ({
      hasUnreadAnnouncements:
        articleUnreadRevision > 0
          ? isUnreadAnnouncementRevision(articleUnreadRevision, readRevision)
          : isUnreadAnnouncement(articleTimestamp, readTimestamp),
      isAnnouncementMetaReady: metaQuery.isSuccess,
      isAnnouncementMetaPending,
      isAnnouncementRead,
      markAnnouncementRead,
      markAnnouncementsReadThrough,
      markCurrentAnnouncementsRead,
    }),
    [
      articleTimestamp,
      articleUnreadRevision,
      isAnnouncementRead,
      markAnnouncementRead,
      markAnnouncementsReadThrough,
      markCurrentAnnouncementsRead,
      metaQuery.isSuccess,
      isAnnouncementMetaPending,
      readTimestamp,
      readRevision,
    ],
  );

  return (
    <AnnouncementStatusContext.Provider value={value}>
      {children}
    </AnnouncementStatusContext.Provider>
  );
};

function retainAnnouncementReadsAfter(
  readAnnouncements: ReadonlyMap<number, SessionAnnouncementRead>,
  publishedAt: string | null,
  articleUnreadRevision: number | null,
): ReadonlyMap<number, SessionAnnouncementRead> {
  const throughEpoch = announcementTimestampEpoch(publishedAt);
  if (
    (throughEpoch === null && articleUnreadRevision === null) ||
    readAnnouncements.size === 0
  ) {
    return readAnnouncements;
  }

  let changed = false;
  const retained = new Map<number, SessionAnnouncementRead>();
  for (const [id, receipt] of readAnnouncements) {
    const readEpoch = announcementTimestampEpoch(receipt.publishedAt);
    const covered =
      receipt.articleUnreadRevision > 0
        ? articleUnreadRevision !== null &&
          receipt.articleUnreadRevision <= articleUnreadRevision
        : throughEpoch !== null &&
          readEpoch !== null &&
          readEpoch <= throughEpoch;
    if (covered) {
      changed = true;
    } else {
      retained.set(id, receipt);
    }
  }
  return changed ? retained : readAnnouncements;
}
